import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiFetch } from "../api/client.js";
import DateRangeNavigator from "../components/DateRangeNavigator.jsx";
import InvoiceDeliveryStatus from "../components/InvoiceDeliveryStatus.jsx";
import PdfInvoicePreview from "../components/PdfInvoicePreview.jsx";
import { downloadAuthenticatedPdf } from "../utils/downloadPdf.js";
import { currentWeekRange, dateEnd, dateStart } from "../utils/date.js";

function runLabel(invoice) {
  const type = invoice.invoiceRun?.type || "Single";

  return type === "Bulk" ? "Batch run" : "Single run";
}

function invoiceRecipientLabel(invoice) {
  if (!invoice) {
    return "";
  }

  return (
    invoice.snapshot?.recipientName ||
    invoice.recipientName ||
    invoice.client?.name ||
    invoice.clients?.map((item) => item.client?.name).filter(Boolean).join(", ") ||
    "Invoice"
  );
}

export default function InvoiceHistoryPage() {
  const history = useHistory();
  const [filters, setFilters] = useState(currentWeekRange);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const load = useCallback(async (nextFilters = filters) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        from: dateStart(nextFilters.startDate).toISOString(),
        to: dateEnd(nextFilters.endDate).toISOString(),
      });
      const invoiceData = await apiFetch(`/invoices?${params}`);

      setInvoices(invoiceData);
      setSelectedInvoice((currentInvoice) => {
        if (!currentInvoice) {
          return null;
        }

        return invoiceData.find((invoice) => invoice.id === currentInvoice.id) || currentInvoice;
      });
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useIonViewWillEnter(() => {
    load();
  });

  function updateFilters(nextFilters) {
    setFilters(nextFilters);
    load(nextFilters);
  }

  async function sendInvoice(invoiceId) {
    try {
      await apiFetch(`/invoices/send/${invoiceId}`, { method: "POST" });

      setSelectedInvoice(null);
      await load();
      setToast("Invoice sent.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function openAuthenticatedPdf(invoice) {
    if (!invoice) {
      return;
    }

    try {
      await downloadAuthenticatedPdf(`/invoices/pdf/${invoice.id}`, `${invoice.invoiceNumber}.pdf`);
    } catch (err) {
      setToast(err.message);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.push("/app/finance")}>Back</IonButton>
          </IonButtons>
          <IonTitle>Invoice History</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <main className="page-pad">
          <DateRangeNavigator value={filters} onChange={updateFilters} />

          {!loading && (
            <section className="list-stack">
              {invoices.map((invoice) => (
                <article className="panel row-card" key={invoice.id}>
                  <div>
                    <h3>{invoice.invoiceNumber}</h3>
                    <p>
                      {runLabel(invoice)} · {new Date(invoice.createdAt).toLocaleString()}
                    </p>
                    <p>{invoiceRecipientLabel(invoice)} · {invoice.status}</p>
                    <InvoiceDeliveryStatus invoice={invoice} compact />
                    <strong>${Number(invoice.total).toFixed(2)}</strong>
                  </div>
                  <div className="row-actions">
                    <IonButton size="small" fill="outline" onClick={() => setSelectedInvoice(invoice)}>
                      Preview
                    </IonButton>
                    {invoice.status === "Draft" && (
                      <IonButton size="small" onClick={() => sendInvoice(invoice.id)}>
                        Send
                      </IonButton>
                    )}
                  </div>
                </article>
              ))}
              {invoices.length === 0 && <div className="empty-state">No invoices in this date range.</div>}
            </section>
          )}
        </main>

        <IonModal isOpen={Boolean(selectedInvoice)} onDidDismiss={() => setSelectedInvoice(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedInvoice?.invoiceNumber || "Invoice"}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedInvoice(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <p>
                  {invoiceRecipientLabel(selectedInvoice)} · {selectedInvoice?.status}
                </p>
                <InvoiceDeliveryStatus invoice={selectedInvoice} />
                <IonButton expand="block" fill="outline" onClick={() => openAuthenticatedPdf(selectedInvoice)}>
                  Download PDF
                </IonButton>
                {selectedInvoice?.status === "Draft" && (
                  <IonButton expand="block" onClick={() => sendInvoice(selectedInvoice.id)}>
                    Send This Invoice
                  </IonButton>
                )}
              </section>
              <PdfInvoicePreview invoiceId={selectedInvoice?.id} />
            </main>
          </IonContent>
        </IonModal>

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
